'use client'

import { useRepoReplicache } from '~/hooks-data/use-repo-replicache'
import { useUserPreferences } from '~/hooks-data/use-user-preferences'
import { sidebarLocalStorageKeys } from '~/lib/dom-keys'
import { ResizableContainer } from '~/sotano-kit/primitives/resizable-container'
import { parseTab, useTabsStore } from '../tabs/store'
import { useUserStore } from '~/app/_store/user-store'
import { useStorage } from '~/hooks/use-storage'
import { useHasRendered } from '~/hooks/use-has-rendered'

export const useRenderDocumentSidebar = () => {
  const activeTab = useTabsStore((s) => s.activeTab)
  const tabs = useTabsStore((s) => s.tabs)

  const noTabsShown =
    tabs.filter((t) => {
      const type = parseTab(t).type
      return type === 'block'
    }).length === 0

  if (noTabsShown || !activeTab) return false

  const parsedTab = parseTab(activeTab)

  switch (parsedTab.type) {
    case 'branches':
    case 'block:diff':
    case 'commit':
    case 'changelog':
    case 'settings':
    case 'dev':
    case 'timeline':
    case 'agents':
      return false
    case 'merge': {
      return true
    }
    default:
      return true
  }
}

export const PropertiesPanelLayout = () => {
  const collapsePropertiesPanel = useUserPreferences(
    (s) => s.collapsePropertiesPanel
  )
  const { data: repoReplicacheData } = useRepoReplicache()
  const optimisticUser = useUserStore((s) => s.optimisticUser)
  const render = useRenderDocumentSidebar()

  if (!render) return <div className="w-2.5 shrink-0" />
  return (
    <ResizableContainer
      minWidth={250}
      maxWidth={400}
      defaultWidth={260}
      widthLocalStorageKey={sidebarLocalStorageKeys.documentSidebarWidth}
      direction="left"
      containerClassName="h-full min-w-[10px] shrink-0"
      noBorderHighlight
      isClosed={collapsePropertiesPanel === true}
      setIsClosed={async (isToggled: boolean) => {
        if (!repoReplicacheData?.replicache || !optimisticUser) return
        await repoReplicacheData.replicache.mutate.setCollapsedPropertiesPanel({
          userId: optimisticUser.id,
          isCollapsed: isToggled,
        })
      }}
    >
      <div
        id="repo-content-sidebar"
        onContextMenu={(e) => e.stopPropagation()}
        className="relative flex-shrink-0 overflow-visible h-full"
      />
    </ResizableContainer>
  )
}

export const useIsPropertiesPanelCollapsed__PUBLIC = () => {
  return useStorage(
    'localStorage',
    (z) => z.boolean(),
    'public-repo-properties-panel-sidebar-collapsed',
    { initialValue: false }
  )
}

export const usePropertiesPanelMode__PUBLIC = () => {
  return useStorage(
    'localStorage',
    (z) => z.enum(['history', 'properties']),
    'public-repo-properties-panel-mode',
    { initialValue: 'properties' }
  )
}

export function PropertiesPanelLayout__PUBLIC() {
  const [isCollapsed, setIsCollapsed] = useIsPropertiesPanelCollapsed__PUBLIC()

  const render = useRenderDocumentSidebar()
  const hasRendered = useHasRendered()

  if (!hasRendered) return null
  if (!render) return <div className="w-2.5 shrink-0" />
  return (
    <ResizableContainer
      minWidth={232}
      maxWidth={400}
      defaultWidth={250}
      widthLocalStorageKey={sidebarLocalStorageKeys.documentSidebarWidth}
      direction="left"
      containerClassName="h-full min-w-[10px] shrink-0"
      noBorderHighlight
      isClosed={isCollapsed === true}
      setIsClosed={(isToggled: boolean) => {
        setIsCollapsed(isToggled)
      }}
    >
      <div
        id="repo-content-sidebar"
        onContextMenu={(e) => e.stopPropagation()}
        className="relative flex-shrink-0 overflow-visible h-full"
      />
    </ResizableContainer>
  )
}
